import {
  moreUrgentRootSessionStatus,
  type RootSessionStatus,
  type RootSessionStripThreadInput,
} from "../rootSessionStrip";

// World and Session liveness rollups: the chrome's answer to "is anything alive
// behind that door?". Everything here is a pure fold over the same per-thread
// inputs the session strip projects — computed per render, persisted nowhere,
// and display-only. A rollup can change what a door LOOKS like, never what a
// door IS: membership, addressing, and selection read none of this.
//
// Membership rule: a thread deliberately silenced or finished — snoozed,
// settled, archived — contributes nothing. Snooze must actually snooze: a
// failed-but-snoozed session lighting its World door would undo the verb the
// user just used.

function contributesLiveness(
  thread: RootSessionStripThreadInput,
): thread is RootSessionStripThreadInput & { readonly status: RootSessionStatus } {
  return (
    thread.status != null &&
    thread.archivedAt === null &&
    thread.snoozedUntil === null &&
    thread.settledAt === null
  );
}

export interface WorldLivenessRollups {
  /** Most urgent live status per World; absence of a key means quiet. */
  readonly byWorldKey: ReadonlyMap<string, RootSessionStatus>;
  /** Most urgent live status among ungoverned (Local) threads. */
  readonly local: RootSessionStatus | null;
}

export const EMPTY_WORLD_LIVENESS: WorldLivenessRollups = {
  byWorldKey: new Map<string, RootSessionStatus>(),
  local: null,
};

export function projectWorldLivenessRollups(
  threads: ReadonlyArray<RootSessionStripThreadInput>,
): WorldLivenessRollups {
  const byWorldKey = new Map<string, RootSessionStatus>();
  let local: RootSessionStatus | null = null;
  for (const thread of threads) {
    if (!contributesLiveness(thread)) continue;
    if (thread.worldKey === null) {
      local = moreUrgentRootSessionStatus(local, thread.status);
      continue;
    }
    const folded = moreUrgentRootSessionStatus(
      byWorldKey.get(thread.worldKey) ?? null,
      thread.status,
    );
    if (folded !== null) byWorldKey.set(thread.worldKey, folded);
  }
  return { byWorldKey, local };
}

export interface SessionLivenessNode {
  readonly sessionId: string;
  readonly parentId: string | null;
  readonly lifecycle?: "open" | "retired" | undefined;
}

export interface SessionLivenessRollup {
  /** Most urgent live status among threads seated on exactly this Session. */
  readonly own: RootSessionStatus | null;
  /** Most urgent live status anywhere in the STRICT descendant Session subtree —
   * the ghost that lets unseen nested work announce itself once on the
   * visible ancestor tab. */
  readonly descendants: RootSessionStatus | null;
}

/**
 * Attribute each live thread to a Session, then fold statuses up the Session tree.
 *
 * Attribution follows the seat law first (exact `sessionId` binding), and for a
 * child thread with no seat of its own, walks `parentThreadId` within the same
 * environment to the nearest seated ancestor — so a decomposed child's failure
 * still reaches the tab of the Session its lineage hangs from. A thread whose
 * binding names a Session this index does not contain stays invisible here:
 * absence is typed by the index, never invented by the fold.
 */
export function projectSessionLivenessRollups(input: {
  readonly worldKey: string;
  readonly sessions: ReadonlyArray<SessionLivenessNode>;
  readonly threads: ReadonlyArray<RootSessionStripThreadInput>;
}): ReadonlyMap<string, SessionLivenessRollup> {
  const sessionIds = new Set(input.sessions.map((slice) => slice.sessionId));
  const retiredSessionIds = new Set(
    input.sessions
      .filter((session) => session.lifecycle === "retired")
      .map((session) => session.sessionId),
  );
  const worldThreads = input.threads.filter((thread) => thread.worldKey === input.worldKey);
  const byIdentity = new Map<string, RootSessionStripThreadInput>();
  for (const thread of worldThreads) {
    byIdentity.set(`${thread.environmentId} ${thread.threadId}`, thread);
  }

  const seatedSessionOf = (thread: RootSessionStripThreadInput): string | null => {
    const visited = new Set<string>();
    let cursor: RootSessionStripThreadInput | undefined = thread;
    while (cursor !== undefined) {
      const key = `${cursor.environmentId} ${cursor.threadId}`;
      if (visited.has(key)) return null;
      visited.add(key);
      if (cursor.sessionId != null && sessionIds.has(cursor.sessionId)) return cursor.sessionId;
      cursor =
        cursor.parentThreadId === null
          ? undefined
          : byIdentity.get(`${cursor.environmentId} ${cursor.parentThreadId}`);
    }
    return null;
  };

  const own = new Map<string, RootSessionStatus | null>();
  for (const thread of worldThreads) {
    if (!contributesLiveness(thread)) continue;
    const sessionId = seatedSessionOf(thread);
    if (sessionId === null || retiredSessionIds.has(sessionId)) continue;
    own.set(sessionId, moreUrgentRootSessionStatus(own.get(sessionId) ?? null, thread.status));
  }

  const childrenByParent = new Map<string, SessionLivenessNode[]>();
  for (const slice of input.sessions) {
    if (slice.parentId === null) continue;
    const siblings = childrenByParent.get(slice.parentId);
    if (siblings === undefined) childrenByParent.set(slice.parentId, [slice]);
    else siblings.push(slice);
  }

  const descendantsMemo = new Map<string, RootSessionStatus | null>();
  const descendantsOf = (
    sessionId: string,
    trail: ReadonlySet<string>,
  ): RootSessionStatus | null => {
    const memoized = descendantsMemo.get(sessionId);
    if (memoized !== undefined) return memoized;
    if (trail.has(sessionId)) return null;
    const nextTrail = new Set(trail).add(sessionId);
    let folded: RootSessionStatus | null = null;
    for (const child of childrenByParent.get(sessionId) ?? []) {
      folded = moreUrgentRootSessionStatus(folded, own.get(child.sessionId) ?? null);
      folded = moreUrgentRootSessionStatus(folded, descendantsOf(child.sessionId, nextTrail));
    }
    descendantsMemo.set(sessionId, folded);
    return folded;
  };

  const rollups = new Map<string, SessionLivenessRollup>();
  for (const slice of input.sessions) {
    rollups.set(slice.sessionId, {
      own: own.get(slice.sessionId) ?? null,
      descendants: descendantsOf(slice.sessionId, new Set()),
    });
  }
  return rollups;
}
